import {useCallback, useEffect, useState} from 'react';

import {type LogLineData} from '../LogLineRenderer';

const escapeRegexChars = (value: string) =>
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export interface LogSearchMatch {
  // row is the index within the provided log lines array that a match is in.
  row: number;
  // col is the index within the log line that the match starts at.
  col: number;
  // lineId is the log line's offset within the run.
  lineId: number;
}

export interface LogSearchMatches {
  ordered: LogSearchMatch[];
  indexed: Record<number, number[]>;
}

export interface LogSearchReturn {
  searchText: string;
  searchActive: number;
  searchMatches: LogSearchMatches;
  onSearchChange: (value: string) => void;
  onSetIndex: (index: number) => void;
  onKeyDown: (key: string) => void;
  goToNext: () => void;
  goToPrev: () => void;
}

/**
 * useLogSearch manages search functionality for log lines, including match
 * tracking and navigation. Provides case-insensitive search with support for
 * using the 'enter' key to navigate through results.
 */
export function useLogSearch(
  filteredLines: readonly LogLineData[],
  scrollTo: (row: number) => void
): LogSearchReturn {
  // Current match focused by the user.
  const [searchActive, setSearchActive] = useState(-1);
  const [searchMatches, setSearchMatches] = useState<LogSearchMatches>({
    ordered: [],
    indexed: {},
  });

  const [searchText, setSearchText] = useState('');

  const onSearchChange = useCallback(
    (value: string) => {
      const matches = calculateMatches(value, filteredLines);

      setSearchText(value);
      setSearchMatches(matches);
      setSearchActive(matches.ordered.length ? 0 : -1);

      if (matches.ordered.length) {
        scrollTo(matches.ordered[0].row);
      }
    },
    [filteredLines, scrollTo]
  );

  // When displayed lines change (e.g. due to pagination), recalculate matches
  // and preserve the user's position by finding the same match they were on.
  useEffect(() => {
    if (!searchText) {
      return;
    }

    // Remember which match the user was previously viewing.
    const activeMatchPrev =
      searchActive >= 0 && searchMatches.ordered[searchActive]
        ? searchMatches.ordered[searchActive]
        : null;

    // Recalculate matches with the new filteredLines.
    const matches = calculateMatches(searchText, filteredLines);
    setSearchMatches(matches);

    // Find the same line in the new matches or fall back to clamped index.
    let activeMatchIndex = -1;
    if (matches.ordered.length) {
      if (activeMatchPrev) {
        activeMatchIndex = matches.ordered.findIndex(
          m =>
            // Identify the match's current row/index based on the log line ID
            // (offset within the run) and the column within the log line text.
            m.lineId === activeMatchPrev.lineId && m.col === activeMatchPrev.col
        );
      } else {
        // Going from zero matches to some matches, so activate the first one.
        activeMatchIndex = 0;
      }

      if (activeMatchIndex === -1) {
        activeMatchIndex = Math.min(searchActive, matches.ordered.length - 1);
      }
    }

    setSearchActive(activeMatchIndex);
    if (!activeMatchPrev && activeMatchIndex > -1) {
      scrollTo(matches.ordered[activeMatchIndex].row);
    }

    // NOTE: Don't depend on search property changes, only log line content, as
    // that's the case that needs to be handled here.
  }, [filteredLines, scrollTo]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSetIndex = useCallback(
    (index: number) => {
      setSearchActive(index);
      scrollTo(searchMatches.ordered[index].row);
    },
    [scrollTo, searchMatches.ordered]
  );

  const goToNext = useCallback(() => {
    if (searchMatches.ordered.length === 0) {
      return;
    }

    const nextIndex = (searchActive + 1) % searchMatches.ordered.length;
    onSetIndex(nextIndex);
  }, [searchActive, searchMatches.ordered.length, onSetIndex]);

  const goToPrev = useCallback(() => {
    if (searchMatches.ordered.length === 0) {
      return;
    }

    const prevIndex =
      (searchActive - 1 + searchMatches.ordered.length) %
      searchMatches.ordered.length;
    onSetIndex(prevIndex);
  }, [searchActive, searchMatches.ordered.length, onSetIndex]);

  const onKeyDown = useCallback(
    (key: string) => {
      if (key === 'Enter') {
        goToNext();
      }
    },
    [goToNext]
  );

  return {
    searchText,
    searchActive,
    searchMatches,
    onSearchChange,
    onSetIndex,
    onKeyDown,
    goToNext,
    goToPrev,
  };
}

function calculateMatches(
  searchText: string,
  filteredLines: readonly LogLineData[]
): LogSearchMatches {
  const matches: LogSearchMatches = {
    ordered: [],
    indexed: {},
  };

  if (!searchText) {
    return matches;
  }

  const regex = new RegExp(escapeRegexChars(searchText), 'ig');

  for (let i = 0; i < filteredLines.length; i++) {
    const text = filteredLines[i].line;

    for (const {index} of text.matchAll(regex)) {
      if (index == null) {
        continue;
      }

      // TODO(WB-29336): Migrate to 'number' property of logs.
      const lineId = parseInt(filteredLines[i].id, 10);
      matches.ordered.push({row: i, col: index, lineId});

      if (i in matches.indexed) {
        matches.indexed[i].push(index);
      } else {
        matches.indexed[i] = [index];
      }
    }
  }

  return matches;
}
